import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type { MembershipPayload } from '@/store/types/MembershipPayload'
import { loadTeamMemberships } from '@/backend/darwin'
import { useToast } from '@/uiKit/Toast/useToast'
import { deleteTeamMembership } from '@/backend/darwin/deleteTeamMembership'
import type { ImagePayload } from '@/store/types/ImagePayload'
import type { TeamPayload } from '@/store/types/TeamPayload'
import type { MembershipRole } from '@/store/types/MembershipRole'
import type { PartialRecord } from '@/core/helperTypes'
import { getFullName } from '@/core/utils/formatter'
import { updateTeamMembership } from '@/backend/darwin/updateTeamMembership'
import {
  getMembershipApiKeyImpact,
  type AffectedApiKey,
} from '@/backend/darwin/getMembershipApiKeyImpact'

const mergeMemberships = (
  membershipsA: MembershipPayload[],
  membershipsB: MembershipPayload[],
): MembershipPayload[] => {
  const newIds = membershipsB.map((d) => d.id)
  return membershipsA.filter((m) => !newIds.includes(m.id)).concat(membershipsB)
}

export const useMembershipStore = defineStore('membership', () => {
  const toast = useToast()

  const memberships = ref<MembershipPayload[]>([])
  const memberIdByUserId = shallowRef<PartialRecord<number, number>>({})
  const userIdByMemberId = shallowRef<PartialRecord<number, number>>({})
  const memberNameById = shallowRef<PartialRecord<number, string>>({})
  const memberAvatarUrlById = shallowRef<PartialRecord<number, string>>({})

  const syncMembershipMaps = (): void => {
    memberIdByUserId.value = {}
    userIdByMemberId.value = {}
    memberNameById.value = {}
    memberAvatarUrlById.value = {}

    memberships.value.forEach((item: MembershipPayload) => {
      memberIdByUserId.value[item.user_id] = item.id
      userIdByMemberId.value[item.id] = item.user_id
      memberNameById.value[item.id] = getFullName(item)
      if (!item.image) {
        return
      }
      memberAvatarUrlById.value[item.id] = item.image.thumbnail_url || item.image.url
    })
  }

  const loadMemberships = async (teamSlug: string): Promise<void> => {
    const response = await loadTeamMemberships({ teamSlug })

    if ('data' in response) {
      memberships.value = mergeMemberships(memberships.value, response.data)
    }
  }

  const loadPartnerMemberships = async (currentTeam: TeamPayload): Promise<void> => {
    if (currentTeam.managed_status !== 'client') {
      return
    }
    if (!currentTeam.partner) {
      return
    }

    const response = await loadTeamMemberships({ teamSlug: currentTeam.partner.slug })

    if ('data' in response) {
      memberships.value = mergeMemberships(memberships.value, response.data)
    }
  }

  const getMembershipsByTeamId = (teamId: number): MembershipPayload[] =>
    memberships.value.filter((m) => m.team_id === teamId)

  /**
   * Used to match a team member currently in the store by their team and user id
   */
  const getMembershipByTeamIdUserId = (teamId: number, userId: number): MembershipPayload | null =>
    memberships.value.find((m) => m.team_id === teamId && m.user_id === userId) || null

  const deleteMembershipById = async (membershipId: MembershipPayload['id']): Promise<void> => {
    const response = await deleteTeamMembership(membershipId)

    if (!response.ok) {
      const message =
        typeof response.error.message === 'string'
          ? response.error.message
          : 'Something went wrong while removing the user from the team'
      toast.warning({ meta: { title: message } })
      return
    }

    memberships.value = memberships.value.filter((membership) => membership.id !== membershipId)
    toast.success({ meta: { title: 'Member successfully removed' } })
  }

  /**
   * Returns the member's API keys that a membership change would affect.
   * Pass `newRole` for a role downgrade, omit it for a removal.
   *
   * Fails open (returns `[]`) so a transient error never blocks the action -
   * the warning is advisory, the real change is re-checked by the backend.
   */
  const getApiKeyImpact = async (
    membershipId: number,
    newRole?: MembershipRole,
  ): Promise<AffectedApiKey[]> => {
    const response = await getMembershipApiKeyImpact(membershipId, newRole)
    return response.ok ? response.data : []
  }

  const updateMembershipAvatar = (membershipId: number, image: ImagePayload | null): void => {
    memberships.value = memberships.value.map((membership) => {
      if (membership.id === membershipId) {
        return { ...membership, image }
      }
      return membership
    })
  }

  const updateMembershipRole = async (
    membershipId: number,
    newRole: MembershipRole,
  ): Promise<{ success: boolean }> => {
    const response = await updateTeamMembership(membershipId, newRole)

    if (!response.ok) {
      const message =
        typeof response.error.message === 'string'
          ? response.error.message
          : 'Something went wrong while removing the user from the team'
      toast.warning({ meta: { title: message } })
      return { success: false }
    }

    memberships.value = memberships.value.map((membership) =>
      membership.id === membershipId ? { ...membership, role: newRole } : membership,
    )
    return { success: true }
  }

  return {
    memberships,
    memberIdByUserId,
    userIdByMemberId,
    memberNameById,
    memberAvatarUrlById,

    loadMemberships,
    getMembershipsByTeamId,
    getMembershipByTeamIdUserId,
    loadPartnerMemberships,
    syncMembershipMaps,
    deleteMembershipById,
    getApiKeyImpact,
    updateMembershipAvatar,
    updateMembershipRole,
  }
})
